import { Match, Switch, createSignal, onMount, type Accessor } from 'solid-js';
import { parseHash } from './pairing/parse-hash';
import { PairingDriver, PeerClosed, type AppState } from './pairing/driver';
import { shouldFallBackToReconnect } from './pairing/fallback';
import {
  ensureWasm,
  newHandshake,
  newReconnect,
  rendezvousId,
  browserFingerprintFromPriv,
  fingerprintOfAgentPub,
  verifyDenyMac,
} from './crypto/wasm-bridge';
import { openRelayWs } from './relay/ws';
import {
  getOrCreateBrowserIdentity,
  getStoredReconnectRecord,
  putStoredReconnectRecord,
  clearReconnectRecord,
} from './crypto/identity';
import { dispatchFrame, pause } from './state/metrics';
import { Header } from './ui/header';
import { Layout } from './ui/layout';
import { ScanPrompt } from './ui/scan-prompt';
import { ErrorScreen } from './ui/error-screen';
import { UnreachableScreen } from './ui/unreachable-screen';

const RELAY_URL: string = (import.meta.env.VITE_RELAY_URL as string | undefined) ?? 'wss://relay.heligraph.dev';

// Only the e2e dev server sets it (e2e/rekey.mjs), so sessions rekey within the scenario's hold.
const rekeyEnv = Number(import.meta.env.VITE_HG_REKEY_INTERVAL_MS);
const REKEY_INTERVAL_OVERRIDE_MS = Number.isFinite(rekeyEnv) && rekeyEnv > 0 ? rekeyEnv : undefined;

// The WASM reports a peer close as an error with exactly this message (heligraph-crypto-wasm).
function receiveFrame(receive: () => Uint8Array | undefined): Uint8Array | null {
  try {
    return receive() ?? null;
  } catch (e) {
    if (e instanceof Error && e.message === 'peer_closed') throw new PeerClosed();
    throw e;
  }
}

function retry(): void {
  window.location.hash = '';
  window.location.reload();
}

// Compares two agent public keys. These are non-secret identifiers, so a plain
// (non-constant-time) byte comparison is fine.
function bytesEqual(a: Uint8Array, b: Uint8Array): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}

export function App() {
  // Driver instance is published via a signal so its reactive `state` accessor
  // can be threaded through a derived accessor (no setInterval polling).
  const [driver, setDriver] = createSignal<PairingDriver | null>(null);
  // Pre-driver state (e.g. malformed_hash detected before a driver exists).
  const [preState, setPreState] = createSignal<AppState>({ kind: 'idle' });

  const state: Accessor<AppState> = () => {
    const d = driver();
    return d ? d.state() : preState();
  };

  // Construct a fully-wired PairingDriver. Shared by the fresh-pairing path and
  // the auto-resume path so both use identical WASM/relay/persistence deps.
  const makeDriver = (): PairingDriver =>
    new PairingDriver({
      ensureWasm,
      openWs: (sid) => openRelayWs(RELAY_URL, sid),
      makeHandshake: (pin, priv, pub) => {
        const b = newHandshake(pin, priv, pub);
        return {
          hs: {
            spake_msg_a: () => b.hs.spake_msg_a(),
            process_spake_msg_b: (x) => b.hs.process_spake_msg_b(x),
            process_noise_msg2: (x) => b.hs.process_noise_msg2(x),
            decrypt_frame: (x) => receiveFrame(() => b.hs.receive_frame(x)),
            reconnect_root: () => b.hs.reconnect_root(),
            encrypt_frame: (x) => b.hs.encrypt_json(x),
            rekey_frame: () => b.hs.rekey_frame(),
            rekey_due: () => b.hs.rekey_due(),
          },
          agentFingerprint: b.agentFingerprint,
        };
      },
      browserFingerprint: browserFingerprintFromPriv,
      dispatchFrame,
      makeReconnect: (root, priv, pub) => {
        const b = newReconnect(root, priv, pub);
        return {
          msg1: () => b.rc.msg1(),
          process_msg2: (x) => b.rc.process_msg2(x),
          decrypt_frame: (x) => receiveFrame(() => b.rc.receive_frame(x)),
          encrypt_frame: (x) => b.rc.encrypt_json(x),
          rekey_frame: () => b.rc.rekey_frame(),
          rekey_due: () => b.rc.rekey_due(),
        };
      },
      rendezvousId,
      persistReconnect: (agentPub, reconnectRoot) =>
        putStoredReconnectRecord({ agent_pub: agentPub, reconnect_root: reconnectRoot }),
      clearReconnect: clearReconnectRecord,
      verifyDenyMac: (root, reason, rid, mac) => verifyDenyMac(root, reason, rid, mac),
      rekeyIntervalMs: REKEY_INTERVAL_OVERRIDE_MS,
    });

  onMount(async () => {
    const parsed = parseHash(window.location.hash);
    const browserPriv = await getOrCreateBrowserIdentity();

    // Valid pairing hash → fresh QR pairing first, so a freshly-scanned QR is
    // never hijacked. But if that first dial fails because the session is gone
    // (stale/bookmarked hash), the room is still busy, or the dial froze (network
    // switch), and we still hold reconnect material for the *same* agent, fall
    // back to a seamless reconnect instead of dead-ending on an error screen.
    // (smoke bug #2; widened for the Wi-Fi→LTE reload case)
    if (parsed.ok) {
      const rec = await getStoredReconnectRecord();
      const d = makeDriver();
      setDriver(d);
      await d.start(parsed.data, browserPriv);
      const st = d.state();
      if (shouldFallBackToReconnect(st) && rec && bytesEqual(rec.agent_pub, parsed.data.agentPub)) {
        // start() already initialized WASM to reach the relay; await is defensive.
        await ensureWasm();
        void d.reconnect(
          rec.reconnect_root,
          browserPriv,
          rec.agent_pub,
          fingerprintOfAgentPub(rec.agent_pub),
        );
      }
      return;
    }

    // No hash → auto-resume from a saved reconnect record if one exists.
    if (parsed.reason === 'no_hash') {
      const rec = await getStoredReconnectRecord();
      if (rec) {
        // fingerprintOfAgentPub() is WASM-backed, so the module must be
        // initialized before it is called (the driver also awaits ensureWasm,
        // but the fingerprint is computed here, before reconnect() runs).
        await ensureWasm();
        const d = makeDriver();
        setDriver(d);
        void d.reconnect(
          rec.reconnect_root,
          browserPriv,
          rec.agent_pub,
          fingerprintOfAgentPub(rec.agent_pub),
        );
        return;
      }
      setPreState({ kind: 'idle' });
      return;
    }

    // Malformed hash.
    setPreState({ kind: 'error', code: 'malformed_hash' });
  });

  return (
    <Switch fallback={<ScanPrompt />}>
      <Match when={state().kind === 'connecting' || state().kind === 'handshaking'}>
        <Connecting state={state()} />
      </Match>
      <Match when={state().kind === 'live' ? state() : null} keyed>
        {(s) => {
          // Type-narrow via the keyed Match guard above.
          if (s.kind !== 'live') return null;
          return (
            <div class="min-h-screen">
              <Header
                browserFp={s.browserFp}
                agentFp={s.agentFp}
                status={pause().paused ? 'paused' : 'live'}
              />
              <Layout />
            </div>
          );
        }}
      </Match>
      <Match when={state().kind === 'reconnecting' ? state() : null} keyed>
        {(s) => {
          if (s.kind !== 'reconnecting') return null;
          return (
            <div class="min-h-screen opacity-60 transition-opacity">
              <Header browserFp={s.browserFp} agentFp={s.agentFp} status="reconnecting" />
              <div class="pointer-events-none">
                <Layout />
              </div>
              <div class="hud-mono fixed inset-x-0 bottom-0 border-t border-warn/40 bg-warn-soft px-4 py-2 text-center text-[0.6875rem] uppercase tracking-[0.2em] text-warn">
                Reconnecting…
              </div>
            </div>
          );
        }}
      </Match>
      <Match when={state().kind === 'unreachable' ? state() : null} keyed>
        {(s) => {
          if (s.kind !== 'unreachable') return null;
          return <UnreachableScreen onRetry={() => driver()?.retryReconnectNow()} />;
        }}
      </Match>
      <Match when={state().kind === 'error' ? state() : null} keyed>
        {(s) => {
          if (s.kind !== 'error') return null;
          return <ErrorScreen code={s.code} detail={s.detail} onRetry={retry} />;
        }}
      </Match>
    </Switch>
  );
}

function Connecting(props: { state: AppState }) {
  const text = () => {
    const k = props.state.kind;
    if (k === 'connecting') return 'Connecting…';
    if (k === 'handshaking') return 'Pairing…';
    return 'Loading…';
  };
  return (
    <main class="flex min-h-screen flex-col items-center justify-center gap-4 p-6 text-center">
      <div class="h-6 w-6 animate-spin rounded-full border-2 border-line border-t-holo" />
      <p class="hud-mono text-[0.6875rem] uppercase tracking-[0.2em] text-muted">{text()}</p>
    </main>
  );
}
